import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { MapContainer, TileLayer, Marker, Popup, useMap, ZoomControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Users, MapPin, Calendar, X, Heart, MessageSquare, Locate, PawPrint } from "lucide-react";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const DEFAULT_CENTER = { lat: 28.5355, lng: 77.3910 }; // Noida (Sector 18)
const CITY_PRESETS = [
  { label: "Noida", lat: 28.5355, lng: 77.3910 },
  { label: "Delhi", lat: 28.6139, lng: 77.2090 },
  { label: "Gurgaon", lat: 28.4595, lng: 77.0266 },
  { label: "Mumbai", lat: 19.0760, lng: 72.8777 },
  { label: "Bengaluru", lat: 12.9716, lng: 77.5946 },
];

function petDivIcon(photo, picked = false) {
  const border = picked ? "#FF6B35" : "#1E9BD8";
  const badge = picked ? '<div style="position:absolute;top:-4px;right:-4px;width:18px;height:18px;border-radius:50%;background:#FF6B35;color:#fff;font-size:12px;font-weight:800;display:flex;align-items:center;justify-content:center;border:2px solid #fff;">✓</div>' : "";
  return L.divIcon({
    className: "",
    iconSize: [44, 44],
    iconAnchor: [22, 44],
    popupAnchor: [0, -42],
    html: `<div style="position:relative;width:44px;height:44px;border-radius:50%;border:3px solid ${border};overflow:visible;background:#FFFDF7;box-shadow:0 6px 18px rgba(0,0,0,.18);transform:translateY(-6px);">
      <img src="${photo}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;" alt=""/>
      ${badge}
      <div style="position:absolute;left:50%;top:100%;transform:translate(-50%,-2px);width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:10px solid ${border};"></div>
    </div>`,
  });
}

function midwayDivIcon() {
  return L.divIcon({
    className: "",
    iconSize: [50, 50],
    iconAnchor: [25, 50],
    popupAnchor: [0, -48],
    html: `<div style="width:50px;height:50px;border-radius:50%;background:#FF6B35;display:flex;align-items:center;justify-content:center;font-size:24px;border:4px solid #fff;box-shadow:0 8px 24px rgba(255,107,53,.5);animation:pulse 2s ease-in-out infinite;">🎯</div><style>@keyframes pulse{0%,100%{transform:scale(1);}50%{transform:scale(1.08);}}</style>`,
  });
}

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.flyTo([center.lat, center.lng], 13, { duration: 0.6 }); }, [center, map]);
  return null;
}

export default function Meetups() {
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [pets, setPets] = useState([]);
  const [meetups, setMeetups] = useState([]);
  const [selected, setSelected] = useState(null);
  const [joined, setJoined] = useState({});
  const [picked, setPicked] = useState([]); // multi-select pet ids
  const [midway, setMidway] = useState(null);
  const [loadingMidway, setLoadingMidway] = useState(false);

  // Auto-centre on the user's GPS once on mount. If permission is denied or
  // the browser doesn't support it, we silently fall back to the city default.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => { /* keep default city centre */ },
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 5 * 60 * 1000 },
    );
  }, []);

  const loadPets = (c) => axios.get(`${API}/community/nearby-pets`, { params: { lat: c.lat, lng: c.lng, radius_km: 25 } }).then(r => setPets(r.data.pets));
  const loadMeetups = () => axios.get(`${API}/meetups`).then(r => setMeetups(r.data));

  useEffect(() => { loadMeetups(); }, []);
  useEffect(() => { loadPets(center); }, [center]);

  const togglePick = (id) => {
    setPicked(p => p.includes(id) ? p.filter(x => x !== id) : (p.length >= 5 ? p : [...p, id]));
    setMidway(null);
  };
  const suggestMidway = async () => {
    setLoadingMidway(true);
    try {
      const r = await axios.post(`${API}/meetups/suggest-midway`, { pet_ids: picked });
      setMidway(r.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not suggest midway");
    } finally { setLoadingMidway(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
    }, () => { /* ignore */ }, { enableHighAccuracy: true, timeout: 6000 });
  };

  const join = async (id) => {
    await axios.post(`${API}/meetups/${id}/join`);
    setJoined(prev => ({ ...prev, [id]: true }));
    loadMeetups();
  };

  return (
    <div className="space-y-6" data-testid="meetups-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Meet pets nearby</h1>
          <p className="text-ink-soft text-sm">Tap any pet on the map to see their profile and say hi 🐾</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="bg-surface border border-line rounded-full p-1 flex">
            {CITY_PRESETS.map(c => (
              <button
                key={c.label}
                data-testid={`city-${c.label.toLowerCase()}`}
                onClick={() => setCenter({ lat: c.lat, lng: c.lng })}
                className={`px-3 py-1.5 rounded-full text-xs font-bold ${Math.abs(center.lat - c.lat) < 0.4 && Math.abs(center.lng - c.lng) < 0.4 ? "bg-ink text-white" : "text-ink-soft"}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <button data-testid="use-my-location" onClick={useMyLocation} className="bg-brand text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5 hover:bg-brand-hover">
            <Locate className="w-4 h-4"/> My location
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4">
        {/* MAP */}
        <div className="rounded-3xl overflow-hidden border border-line shadow-soft relative bg-surface-muted" style={{ height: "min(62vh, 560px)" }} data-testid="meetups-map">
          <MapContainer center={[center.lat, center.lng]} zoom={13} style={{ height: "100%", width: "100%" }} zoomControl={false}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM'/>
            <Recenter center={center}/>
            {pets.map(p => (
              <Marker
                key={p.community_pet_id}
                position={[p.home_lat, p.home_lng]}
                icon={petDivIcon(p.photo, picked.includes(p.community_pet_id))}
                eventHandlers={{ click: () => setSelected(p) }}
              >
                <Popup>
                  <div style={{ minWidth: 180 }}>
                    <div style={{ fontWeight: 800, fontSize: 14 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: "#5E5A54" }}>{p.breed} · {p.age_years}y</div>
                    <div style={{ fontSize: 11, color: "#5E5A54", marginTop: 4 }}>{p.neighbourhood}</div>
                    <button onClick={(e) => { e.stopPropagation(); togglePick(p.community_pet_id); }} style={{marginTop:6,background:picked.includes(p.community_pet_id)?'#FF6B35':'#1E9BD8',color:'#fff',border:0,borderRadius:999,padding:'4px 10px',fontWeight:700,fontSize:11,cursor:'pointer'}}>
                      {picked.includes(p.community_pet_id) ? "✓ Picked" : "+ Pick for meetup"}
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
            {midway && (
              <Marker position={[midway.midpoint.lat, midway.midpoint.lng]} icon={midwayDivIcon()}>
                <Popup>
                  <div style={{ minWidth: 200 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: "#FF6B35" }}>🎯 AI midway suggestion</div>
                    <div style={{ fontWeight: 800, fontSize: 13, marginTop: 4 }}>{midway.venue}</div>
                    <div style={{ fontSize: 11, color: "#5E5A54", marginTop: 4 }}>{midway.rationale}</div>
                    <div style={{ fontSize: 11, fontWeight: 800, marginTop: 4 }}>⏰ {midway.suggested_time}</div>
                  </div>
                </Popup>
              </Marker>
            )}
            <ZoomControl position="topright"/>
          </MapContainer>

          {/* Top-left nearby badge */}
          <div className="absolute top-3 left-3 z-[400] bg-surface/95 backdrop-blur rounded-full pl-2 pr-3 py-1.5 shadow-soft border border-line inline-flex items-center gap-2 pointer-events-none">
            <div className="w-7 h-7 rounded-full bg-brand/15 grid place-items-center"><PawPrint className="w-4 h-4 text-brand"/></div>
            <span className="text-xs font-extrabold leading-none">{pets.length} pets nearby</span>
          </div>

          {/* Recenter / my location button */}
          <button
            data-testid="meetups-recenter"
            onClick={useMyLocation}
            className="absolute bottom-20 right-3 z-[400] bg-surface/95 backdrop-blur w-11 h-11 rounded-full shadow-hover border border-line grid place-items-center hover:bg-brand hover:text-white hover:border-brand transition-colors"
            title="Centre on my location"
          >
            <Locate className="w-4 h-4"/>
          </button>

          {/* Helper hint when no pet picked */}
          {picked.length === 0 && pets.length > 0 && (
            <div className="absolute bottom-3 left-3 z-[400] bg-ink/85 text-white backdrop-blur rounded-full px-3 py-1.5 shadow-soft text-[11px] font-bold pointer-events-none">
              Tap a pet to view profile · pick 2+ for AI midway
            </div>
          )}

          {picked.length > 0 && (
            <div className="absolute bottom-3 left-3 right-3 z-[400] bg-surface/95 backdrop-blur rounded-2xl px-3 py-2 shadow-hover border border-brand flex items-center justify-between gap-2 flex-wrap" data-testid="midway-bar">
              <div className="text-xs font-bold">{picked.length} pet{picked.length === 1 ? "" : "s"} picked {picked.length < 2 && <span className="text-ink-soft font-normal">· pick 1 more</span>}</div>
              <div className="flex gap-2">
                <button onClick={() => { setPicked([]); setMidway(null); }} className="bg-surface-muted rounded-full px-3 py-1.5 text-xs font-bold hover:bg-line" data-testid="midway-clear">Clear</button>
                <button data-testid="midway-suggest" onClick={suggestMidway} disabled={picked.length < 2 || loadingMidway} className="bg-brand text-white rounded-full px-4 py-1.5 text-xs font-bold disabled:opacity-50 hover:bg-brand-hover inline-flex items-center gap-1">
                  {loadingMidway ? "…" : <>🎯 AI midway suggestion</>}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* SIDE — nearby list */}
        <aside className="bg-surface border border-line rounded-3xl shadow-soft overflow-hidden flex flex-col" style={{ maxHeight: "62vh" }}>
          <div className="p-4 border-b border-line">
            <h2 className="font-heading text-lg font-bold flex items-center gap-2"><PawPrint className="w-4 h-4 text-brand"/> Nearby pets</h2>
          </div>
          <div className="overflow-y-auto p-3 space-y-2">
            {pets.length === 0 && <div className="text-ink-soft text-sm">No pets in this area yet.</div>}
            {pets.map(p => (
              <button
                key={p.community_pet_id}
                data-testid={`nearby-pet-${p.community_pet_id}`}
                onClick={() => setSelected(p)}
                className="w-full text-left flex items-center gap-3 bg-surface-muted rounded-2xl p-2 pr-3 hover:bg-brand/10 transition-colors"
              >
                <img src={p.photo} alt={p.name} className="w-12 h-12 rounded-2xl object-cover"/>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{p.name} <span className="text-ink-soft font-normal text-xs">· {p.breed}</span></div>
                  <div className="text-xs text-ink-soft truncate flex items-center gap-1"><MapPin className="w-3 h-3"/>{p.neighbourhood}</div>
                </div>
                {p.likes_meetups && <span className="text-[10px] font-bold uppercase tracking-wider bg-leaf/30 rounded-full px-2 py-0.5">Open to meet</span>}
              </button>
            ))}
          </div>
        </aside>
      </div>

      {/* MEETUPS LIST */}
      <section>
        <h2 className="font-heading text-2xl font-extrabold mb-3">Upcoming meetups</h2>
        <div className="grid md:grid-cols-2 gap-5">
          {meetups.map(m => (
            <article key={m.meetup_id} data-testid={`meetup-${m.meetup_id}`} className="bg-surface border border-line rounded-3xl overflow-hidden shadow-soft">
              <img src={m.photo} alt={m.title} className="w-full h-40 object-cover"/>
              <div className="p-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-heading text-xl font-extrabold">{m.title}</h3>
                  <span className="text-xs uppercase tracking-wider font-bold bg-surface-muted px-2 py-1 rounded-full">{m.species_focus}</span>
                </div>
                <div className="mt-2 text-sm text-ink-soft space-y-1">
                  <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5"/>{m.location} · {m.city}</div>
                  <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5"/>{new Date(m.date_time).toLocaleString()}</div>
                  <div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5"/>{m.attendees} going · hosted by {m.host}</div>
                </div>
                <button
                  data-testid={`join-${m.meetup_id}`}
                  onClick={() => join(m.meetup_id)}
                  disabled={joined[m.meetup_id]}
                  className={`mt-4 w-full rounded-full py-2.5 font-bold text-sm transition-colors ${joined[m.meetup_id] ? "bg-leaf text-ink" : "bg-brand text-white hover:bg-brand-hover"}`}
                >
                  {joined[m.meetup_id] ? "You're going ✓" : "Join meetup"}
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* PET DETAIL DRAWER */}
      {selected && <PetDetailModal pet={selected} onClose={() => setSelected(null)}/>}
    </div>
  );
}

function PetDetailModal({ pet, onClose }) {
  const [greeted, setGreeted] = useState(false);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-end sm:place-items-center p-0 sm:p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-surface w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-line shadow-soft overflow-hidden" data-testid="pet-detail-modal">
        <div className="relative h-56">
          <img src={pet.photo} alt={pet.name} className="w-full h-full object-cover"/>
          <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 bg-surface/90 rounded-full grid place-items-center shadow-soft hover:bg-surface" data-testid="close-pet-detail"><X className="w-4 h-4"/></button>
          {pet.likes_meetups && (
            <div className="absolute top-3 left-3 bg-leaf text-ink rounded-full px-3 py-1 text-[11px] font-bold inline-flex items-center gap-1">
              <Heart className="w-3 h-3 fill-ink"/> Open to meet
            </div>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-end justify-between gap-3">
            <div>
              <h2 className="font-heading text-3xl font-extrabold leading-none">{pet.name}</h2>
              <div className="text-sm text-ink-soft mt-1">{pet.breed} · {pet.age_years}y · {pet.gender}</div>
            </div>
            <span className="text-xs uppercase tracking-wider font-bold bg-surface-muted px-2 py-1 rounded-full">{pet.species}</span>
          </div>

          <div className="mt-3 flex items-center gap-2 text-sm text-ink-soft">
            <MapPin className="w-4 h-4"/>{pet.neighbourhood} · {pet.city}
          </div>

          <p className="mt-4 text-[15px] leading-relaxed">{pet.bio}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            <Chip>Playstyle: {pet.playstyle}</Chip>
            {pet.likes_meetups ? <Chip color="leaf">Loves meetups</Chip> : <Chip color="muted">Prefers solo</Chip>}
          </div>

          <div className="mt-5 bg-surface-muted rounded-2xl p-3 flex items-center gap-3">
            {pet.owner_picture ? (
              <img src={pet.owner_picture} className="w-10 h-10 rounded-full object-cover" alt=""/>
            ) : (
              <div className="w-10 h-10 rounded-full bg-leaf grid place-items-center text-ink font-bold">{pet.owner_name?.[0]}</div>
            )}
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider font-bold text-ink-soft">Owner</div>
              <div className="font-bold">{pet.owner_name}</div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2">
            <button
              data-testid="say-hi-button"
              disabled={!pet.likes_meetups || greeted}
              onClick={() => setGreeted(true)}
              className={`rounded-full py-3 font-bold text-sm inline-flex items-center justify-center gap-2 ${greeted ? "bg-leaf text-ink" : (pet.likes_meetups ? "bg-brand text-white hover:bg-brand-hover" : "bg-surface-muted text-ink-soft cursor-not-allowed")}`}
            >
              <Heart className="w-4 h-4"/> {greeted ? "Hi sent!" : "Say hi"}
            </button>
            <button
              data-testid="invite-meetup-button"
              disabled={!pet.likes_meetups}
              className={`rounded-full py-3 font-bold text-sm inline-flex items-center justify-center gap-2 border ${pet.likes_meetups ? "bg-surface border-line hover:border-brand" : "bg-surface-muted border-line text-ink-soft cursor-not-allowed"}`}
            >
              <MessageSquare className="w-4 h-4"/> Invite to meetup
            </button>
          </div>
          {!pet.likes_meetups && (
            <p className="text-xs text-ink-soft mt-3 text-center">{pet.name}'s owner has marked them as not open to meetups right now.</p>
          )}
        </div>
      </div>
    </div>
  );
}

const Chip = ({ children, color }) => {
  const cls = color === "leaf" ? "bg-leaf/40 text-ink" : color === "muted" ? "bg-surface-muted text-ink-soft" : "bg-sunshine/30 text-ink";
  return <span className={`text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 ${cls}`}>{children}</span>;
};
